/**
 * Tabs Settings Modal
 *
 * Modal for configuring tab behavior preferences.
 */

import {
  DEFAULT_USER_SETTINGS,
  loadUserSettings,
  resetUserSettings,
  saveUserSettings,
  type UserTabSettings,
} from '@/context/TabsContext/settings';
import {
  InfoCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Divider,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
  message,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

const { Text, Title } = Typography;

type TabsSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  onSettingsChange?: (settings: UserTabSettings) => void;
};

export const TabsSettingsModal: React.FC<TabsSettingsModalProps> = ({
  open,
  onClose,
  onSettingsChange,
}) => {
  const [form] = Form.useForm<UserTabSettings>();
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings when modal opens
  useEffect(() => {
    if (open) {
      const settings = loadUserSettings();
      form.setFieldsValue(settings);
      setHasChanges(false);
    }
  }, [open, form]);

  const handleValuesChange = useCallback(() => {
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(() => {
    const values = form.getFieldsValue();
    saveUserSettings(values);
    onSettingsChange?.(values);
    message.success('Tab settings saved');
    setHasChanges(false);
    onClose();
  }, [form, onSettingsChange, onClose]);

  const handleReset = useCallback(() => {
    Modal.confirm({
      title: 'Reset to Defaults',
      content: 'Are you sure you want to reset all tab settings to their default values?',
      okText: 'Reset',
      okType: 'danger',
      onOk: () => {
        const defaults = resetUserSettings();
        form.setFieldsValue(defaults);
        onSettingsChange?.(defaults);
        message.success('Settings reset to defaults');
        setHasChanges(false);
      },
    });
  }, [form, onSettingsChange]);

  const handleCancel = useCallback(() => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Close',
        onOk: onClose,
      });
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          <span>Tab Settings</span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      width={520}
      data-testid="tab-settings-modal"
      footer={[
        <Button key="reset" onClick={handleReset} data-testid="tab-settings-reset">
          Reset to Defaults
        </Button>,
        <Button key="cancel" onClick={handleCancel} data-testid="tab-settings-cancel">
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} data-testid="tab-settings-save">
          Save Settings
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 14 }}
        wrapperCol={{ span: 10 }}
        onValuesChange={handleValuesChange}
        initialValues={DEFAULT_USER_SETTINGS}
      >
        {/* Tab Limits */}
        <Title level={5} style={{ marginTop: 0 }}>Tab Limits</Title>
        
        <Form.Item
          name="maxTabs"
          label={
            <Space>
              <span>Maximum open tabs</span>
              <Tooltip title="When limit is reached, oldest non-pinned tab will be closed">
                <InfoCircleOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />
              </Tooltip>
            </Space>
          }
        >
          <InputNumber min={1} max={30} style={{ width: '100%' }} data-testid="tab-settings-max-tabs" />
        </Form.Item>

        <Divider />

        {/* Persistence */}
        <Title level={5}>Session Persistence</Title>

        <Form.Item
          name="persistRecentTabs"
          label="Restore tabs on login"
          valuePropName="checked"
        >
          <Switch data-testid="tab-settings-persist-tabs" />
        </Form.Item>

        <Form.Item
          name="maxRestoredTabs"
          label="Maximum tabs to restore"
          tooltip="How many non-pinned tabs to restore when you log back in"
        >
          <InputNumber min={1} max={20} style={{ width: '100%' }} data-testid="tab-settings-max-restored" />
        </Form.Item>

        <Divider />

        {/* Performance */}
        <Title level={5}>Performance</Title>

        <Form.Item
          name="enableContentCaching"
          label={
            <Space>
              <span>Enable tab caching</span>
              <Tooltip title="Keep tab contents in memory when switching tabs for faster navigation">
                <InfoCircleOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />
              </Tooltip>
            </Space>
          }
          valuePropName="checked"
        >
          <Switch data-testid="tab-settings-enable-caching" />
        </Form.Item>

        <Form.Item
          name="maxCachedTabs"
          label="Maximum cached tabs"
          tooltip="Number of inactive tabs to keep in memory"
        >
          <InputNumber min={2} max={10} style={{ width: '100%' }} data-testid="tab-settings-max-cached" />
        </Form.Item>

        <Divider />

        {/* Behavior */}
        <Title level={5}>Behavior</Title>

        <Form.Item
          name="defaultBehavior"
          label="Sidebar menu click"
          tooltip="What happens when you click a menu item in the sidebar"
        >
          <Select>
            <Select.Option value="reuse">Reuse existing tab</Select.Option>
            <Select.Option value="new-tab">Always open new tab</Select.Option>
            <Select.Option value="replace">Replace current tab</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="doubleClickAction"
          label="Double-click tab"
          tooltip="Action when double-clicking a tab"
        >
          <Select>
            <Select.Option value="pin">Toggle pin</Select.Option>
            <Select.Option value="none">Do nothing</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="confirmCloseMultiple"
          label="Confirm closing multiple tabs"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider />

        {/* Features */}
        <Title level={5}>Features</Title>

        <Form.Item
          name="enableKeyboardShortcuts"
          label={
            <Space>
              <span>Keyboard shortcuts</span>
              <Tooltip title="Ctrl+W to close, Ctrl+Tab to switch, etc.">
                <InfoCircleOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />
              </Tooltip>
            </Space>
          }
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="enableDragReorder"
          label="Drag to reorder tabs"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>

      <Divider />

      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <strong>Keyboard Shortcuts:</strong>
          <br />
          • Ctrl/Cmd + W: Close current tab
          <br />
          • Ctrl/Cmd + Shift + T: Reopen last closed tab
          <br />
          • Ctrl/Cmd + Tab: Next tab
          <br />
          • Ctrl/Cmd + 1-9: Jump to tab
          <br />
          • Ctrl/Cmd + Y: Toggle split right
          <br />
          • Ctrl/Cmd + U: Toggle split down
          <br />
          • Ctrl/Cmd + Click: Open link in new tab
        </Text>
      </div>
    </Modal>
  );
};

export default TabsSettingsModal;
